import React, { useState, useEffect } from 'react';
import { Shield, Save, AlertCircle, CheckCircle, Info, Lock } from 'lucide-react';
import { roleService } from '../../../services/roleService';
import { useAuth } from '../../../contexts/AuthContext';
import { getHighestHierarchyLevel } from '../../../utils/permissions';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

export default function RoleAccessControlSection() {
  const { userRoles } = useAuth();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const userHierarchyLevel = getHighestHierarchyLevel(userRoles);

  const features = [
    { key: 'accounts', label: 'Accounts' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'deals', label: 'Deals' },
    { key: 'pipeline', label: 'Pipeline' },
    { key: 'activities', label: 'Activities' },
    { key: 'calendar', label: 'Calendar' },
    { key: 'customers', label: 'Customers' },
    { key: 'services', label: 'Services' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'reports', label: 'Reports' },
    { key: 'emails', label: 'Emails' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'users', label: 'User Management' },
    { key: 'billing', label: 'Billing' },
    { key: 'settings', label: 'Settings' },
    { key: 'integrations', label: 'Integrations' }
  ];

  const accessLevels = [
    { key: 'none', label: 'None', color: 'text-gray-600' },
    { key: 'read', label: 'Read', color: 'text-blue-600' },
    { key: 'write', label: 'Write', color: 'text-green-600' },
    { key: 'delete', label: 'Delete', color: 'text-orange-600' },
    { key: 'admin', label: 'Admin', color: 'text-purple-600' }
  ];

  useEffect(() => {
    loadRolesAndPermissions();
  }, []);

  const loadRolesAndPermissions = async () => {
    try {
      setLoading(true);
      const rolesData = await roleService?.getAllRoles();
      
      // Sort by hierarchy level descending (highest first)
      const sortedRoles = rolesData?.sort((a, b) => b?.hierarchyLevel - a?.hierarchyLevel) || [];
      setRoles(sortedRoles);

      // Load permissions for each role
      const permissionsMap = {};
      for (const role of sortedRoles) {
        const rolePerms = await roleService?.getRolePermissions(role?.id);
        permissionsMap[role?.id] = rolePerms?.reduce((acc, perm) => {
          acc[perm?.feature] = perm?.accessLevel;
          return acc;
        }, {});
      }
      setPermissions(permissionsMap);
    } catch (error) {
      console.error('Error loading roles and permissions:', error);
      setErrorMessage('Failed to load role access control data');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (roleId, feature, newAccessLevel) => {
    setPermissions(prev => ({
      ...prev,
      [roleId]: {
        ...prev?.[roleId],
        [feature]: newAccessLevel
      }
    }));
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleSavePermissions = async (roleId) => {
    try {
      setSaving(true);
      setErrorMessage('');
      
      const rolePermissions = permissions?.[roleId] || {};
      const permissionsArray = Object.entries(rolePermissions)?.map(([feature, accessLevel]) => ({
        feature,
        accessLevel
      }));

      await roleService?.updateRolePermissions(roleId, permissionsArray);
      setSuccessMessage('Permissions updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving permissions:', error);
      setErrorMessage('Failed to save permissions: ' + error?.message);
    } finally {
      setSaving(false);
    }
  };

  const canEditRole = (role) => {
    // Super Admins can edit all roles
    if (userHierarchyLevel >= 100) return true;
    
    // Users can only edit roles below their hierarchy level
    return role?.hierarchyLevel < userHierarchyLevel;
  };

  const getHierarchyLevelBadge = (level) => {
    if (level >= 100) return { color: 'bg-red-100 text-red-800 border-red-300', label: 'Super Admin' };
    if (level >= 80) return { color: 'bg-purple-100 text-purple-800 border-purple-300', label: 'Business Admin' };
    if (level >= 60) return { color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Manager' };
    if (level >= 40) return { color: 'bg-green-100 text-green-800 border-green-300', label: 'Staff' };
    return { color: 'bg-gray-100 text-gray-800 border-gray-300', label: 'Customer' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              Role Hierarchy & Access Control
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Manage feature access levels for each role. Users cannot assign roles or permissions higher than their own hierarchy level.
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Invitation Restrictions:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Business Administrators cannot invite Super Admins</li>
                <li>Users cannot invite anyone to a role higher than their own hierarchy level</li>
                <li>Super Admins have unrestricted access to all features and roles</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm font-medium text-green-900">{successMessage}</p>
          </div>
        </div>
      )}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm font-medium text-red-900">{errorMessage}</p>
          </div>
        </div>
      )}
      {/* Roles Table */}
      {roles?.map((role) => {
        const hierarchyBadge = getHierarchyLevelBadge(role?.hierarchyLevel);
        const isEditable = canEditRole(role);
        const rolePermissions = permissions?.[role?.id] || {};

        return (
          <div key={role?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Role Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-gray-600" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{role?.displayName}</h3>
                    <p className="text-sm text-gray-600">{role?.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${hierarchyBadge?.color}`}>
                    {hierarchyBadge?.label} (Level {role?.hierarchyLevel})
                  </span>
                  {!isEditable && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Lock className="w-4 h-4" />
                      <span>Read-only</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Permissions Grid */}
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        System / Feature
                      </th>
                      {accessLevels?.map((level) => (
                        <th key={level?.key} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {level?.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {features?.map((feature) => {
                      const currentAccessLevel = rolePermissions?.[feature?.key] || 'none';
                      
                      return (
                        <tr key={feature?.key} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {feature?.label}
                          </td>
                          {accessLevels?.map((level) => (
                            <td key={level?.key} className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center">
                                <Checkbox
                                  checked={currentAccessLevel === level?.key}
                                  onChange={() => handlePermissionChange(role?.id, feature?.key, level?.key)}
                                  disabled={!isEditable}
                                  className="cursor-pointer"
                                />
                              </div>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Save Button */}
              {isEditable && (
                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={() => handleSavePermissions(role?.id)}
                    disabled={saving}
                    className="flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Permissions'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

